import { useState } from 'react';
import { Ban } from 'lucide-react';
import { BAN_DURATION_DAYS, BAN_REASON_MAX_LENGTH } from '@app/common';
import type { AdminUserSummary } from '@app/common';

import { Button } from '../core/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../core/Dialog';
import { useTranslation } from '../../context/LanguageContext';
import { banUser } from '../../api/admin.api';

/** `null` is the indefinite option, kept distinct from a day count of any size. */
type Duration = number | null;

/**
 * Confirms a suspension and collects the reason shown to the banned user.
 *
 * The reason is mandatory rather than optional: it is the only thing the suspended
 * user is told, and an unexplained lockout generates a support ticket the admin
 * then has to answer from memory.
 */
export function BanUserDialog({
  user,
  onClose,
  onBanned,
}: Readonly<{
  user: AdminUserSummary;
  onClose: () => void;
  onBanned: () => void;
}>) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [days, setDays] = useState<Duration>(BAN_DURATION_DAYS[0]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedReason = reason.trim();

  const submit = async () => {
    if (!trimmedReason || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await banUser(user.id, { reason: trimmedReason, ...(days === null ? {} : { days }) });
      onBanned();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.banFailed'));
      setSubmitting(false);
    }
  };

  const options: Duration[] = [...BAN_DURATION_DAYS, null];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-500/15">
              <Ban className="h-5 w-5 text-rose-400" />
            </div>
            <DialogTitle>{t('admin.banTitle')}</DialogTitle>
          </div>
          <DialogDescription className="pt-3 text-slate-400">
            {t('admin.banDescription', {
              user: user.displayName ?? user.email ?? user.id.slice(0, 8),
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          <div>
            <label
              htmlFor="ban-duration"
              className="text-[10px] font-bold uppercase tracking-wider text-slate-500"
            >
              {t('admin.banDuration')}
            </label>
            <div id="ban-duration" className="mt-2 flex flex-wrap gap-2">
              {options.map((option) => (
                <button
                  key={option ?? 'indefinite'}
                  type="button"
                  onClick={() => setDays(option)}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    days === option
                      ? 'border-rose-500 bg-rose-500/15 text-rose-300'
                      : 'border-slate-700 text-slate-400 hover:bg-slate-700'
                  }`}
                >
                  {option === null
                    ? t('admin.banIndefinite')
                    : t('admin.banDays', { days: option })}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor="ban-reason"
              className="text-[10px] font-bold uppercase tracking-wider text-slate-500"
            >
              {t('admin.banReason')}
            </label>
            <textarea
              id="ban-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={BAN_REASON_MAX_LENGTH}
              rows={3}
              placeholder={t('admin.banReasonPlaceholder')}
              className="mt-2 w-full resize-none rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-200 placeholder:text-slate-500 focus:border-violet-500 focus:outline-none"
            />
            <p className="mt-1 text-[10px] text-slate-500">{t('admin.banReasonHint')}</p>
          </div>

          {error && <p className="text-xs text-rose-400">{error}</p>}
        </div>

        <div className="mt-6 flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            onClick={submit}
            disabled={!trimmedReason || submitting}
          >
            {submitting ? t('common.loading') : t('admin.banConfirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
